import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Crown, Trophy, Star, Globe2, Flame, ChevronRight, Shield, Flag, Users, Swords, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { Loader, Empty } from "../components/UI";

const CONTAINER = "mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12";
const WIDE = "mx-auto max-w-[1880px] px-4 sm:px-8 lg:px-12";
const TABS = ["Overview", "Qualification", "Groups", "Bracket", "Champion"];
const JOURNEY = ["Qualification", "Group Stage", "Round of 32", "Round of 16", "Quarterfinals", "Semifinals", "Final", "World Champion"];

function PlayerChip({ p, dim, wrap, className = "" }) {
  if (!p || !p.display_name) return <span className="text-slate-600">—</span>;
  return (
    <Link to={p.wsbf_player_id ? `/player/${p.wsbf_player_id}` : "#"}
      className={`inline-flex items-center gap-1.5 max-w-full min-w-0 flex-wrap hover:text-[#E5B842] ${dim ? "text-slate-400" : "text-slate-100"} ${className}`}
      style={{ transition: "color 0.15s ease" }}>
      <span className="font-semibold break-words min-w-0">{p.display_name}</span>
      {p.country_iso3 && <span className="text-[10px] font-mono text-slate-500 shrink-0">{p.country_iso3}</span>}
    </Link>
  );
}

export default function WorldChampionshipPage() {
  const [hub, setHub] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [qual, setQual] = useState(null);
  const [groups, setGroups] = useState(null);
  const [bracket, setBracket] = useState(null);
  const [champ, setChamp] = useState(null);
  const [groupBand, setGroupBand] = useState("all");

  useEffect(() => { api.get("/wic").then(({ data }) => setHub(data)).catch(() => setHub({ exists: false })); }, []);
  useEffect(() => {
    if (tab === "Qualification" && !qual) api.get("/wic/qualification").then(({ data }) => setQual(data)).catch(() => setQual({ field: [] }));
    if (tab === "Groups" && !groups) api.get("/wic/groups").then(({ data }) => setGroups(data.groups)).catch(() => setGroups([]));
    if (tab === "Bracket" && !bracket) api.get("/wic/bracket").then(({ data }) => setBracket(data.rounds)).catch(() => setBracket([]));
    if (tab === "Champion") {
      if (!champ) api.get("/wic/champion").then(({ data }) => setChamp(data)).catch(() => setChamp({ crowned: false }));
      if (!qual) api.get("/wic/qualification").then(({ data }) => setQual(data)).catch(() => {});
    }
  }, [tab]); // eslint-disable-line

  // id → player lookup (from the 64-player field) to resolve champion-path opponents without N+1 calls.
  const idMap = useMemo(() => {
    const m = {};
    (qual?.field || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [qual]);

  if (!hub) return <Loader label="Loading World Players Championship…" />;
  if (!hub.exists) return <div className={`${CONTAINER} py-16 animate-fade-up`}><Empty label="The championship has not been drawn yet." /></div>;

  const cfg = hub.config || {};
  const completed = hub.status === "COMPLETED";

  return (
    <div className="animate-fade-up pb-20 overflow-x-hidden">
      {/* FLAGSHIP HERO */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.16] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(70% 120% at 50% -10%, rgba(212,175,55,0.24) 0%, transparent 55%)" }} />
        <div className="absolute left-1/2 -translate-x-1/2 top-0 opacity-[0.1] pointer-events-none" aria-hidden="true"
          style={{ width: 820, height: 340, background: "conic-gradient(from 180deg at 50% 0%, transparent 70deg, #E5B842 90deg, transparent 110deg)" }} />
        <div className={`${CONTAINER} relative py-12 sm:py-16`}>
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#E5B842] font-mono font-bold mb-3 flex items-center gap-2"><Globe2 className="w-3.5 h-3.5" /> WSTF · ArenaBook · World Finals</div>
          <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-slate-50 leading-[1.05] break-words wsbf-slant-block">World Players Championship {hub.edition}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[12px] uppercase tracking-wider text-slate-300">
            <span><b className="text-[#E5B842]">{hub.field_size}</b> Players</span><span className="text-slate-700">·</span>
            <span><b className="text-[#E5B842]">{cfg.groups}</b> Groups</span><span className="text-slate-700">·</span>
            <span><b className="text-[#E5B842]">{cfg.groups * cfg.advance_per_group}</b> Advance</span><span className="text-slate-700">·</span>
            <span><b className="text-[#E5B842]">1</b> World Champion</span>
          </div>
        </div>
      </section>

      <div className={`${CONTAINER} pt-6`}>
        {/* CHAMPION STRIP */}
        {completed && hub.champion && (
          <div data-testid="wic-champion-banner" className="relative overflow-hidden mb-6 rounded-2xl foil-border bg-gradient-to-r from-[#2a2410] to-[#070707] p-6 flex items-center gap-4">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 160% at 8% 0%, rgba(212,175,55,0.2) 0%, transparent 60%)" }} />
            <Crown className="relative w-11 h-11 text-[#E5B842] shrink-0" />
            <div className="relative min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E5B842] font-mono">World Champion {hub.edition}</div>
              <div className="font-heading text-2xl sm:text-3xl font-black text-slate-50 leading-[1.05] mt-0.5 break-words"><PlayerChip p={hub.champion} wrap /></div>
              {hub.finalist && <div className="text-sm text-slate-400 mt-1">def. {hub.finalist.display_name} ({hub.finalist.country_iso3}) in the final</div>}
            </div>
          </div>
        )}

        {/* TABS */}
        <div className="flex gap-1 border-b border-[#2b2512] mb-7 overflow-x-auto no-scrollbar" data-testid="wic-tabs">
          {TABS.map((t) => (
            <button key={t} data-testid={`wic-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm font-heading font-bold uppercase tracking-wide border-b-2 whitespace-nowrap ${tab === t ? "border-[#E5B842] text-[#E5B842]" : "border-transparent text-slate-400 hover:text-slate-200"}`}
              style={{ transition: "color 0.15s ease" }}>{t}</button>
          ))}
        </div>
      </div>

      {/* OVERVIEW */}
      {tab === "Overview" && (
        <div className={CONTAINER}>
          <div data-testid="wic-overview" className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <Metric label="Championship Status" value={hub.status} Icon={Shield} gold />
            <Metric label="Field" value={`${hub.field_size} players`} Icon={Users} />
            <Metric label="Duels Completed" value={`${hub.counts.duels_resolved}/${hub.counts.duels_total}`} Icon={Swords} />
            <Metric label="Knockout Ties" value={hub.counts.ties} Icon={Trophy} />
          </div>
          <div className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-5 mb-10">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-1">Qualification Cut-off</div>
            <div className="font-heading text-xl font-bold text-slate-50">{new Date(hub.cut_off_at).toLocaleString()}</div>
          </div>

          {/* CHAMPIONSHIP JOURNEY */}
          <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-5">The Championship Journey</h2>
          <div className="flex flex-wrap items-center gap-2" data-testid="wic-journey">
            {JOURNEY.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`px-3 py-2 rounded-lg border font-mono text-[11px] uppercase tracking-wider ${i === JOURNEY.length - 1 ? "border-[#E5B842]/50 bg-[#140f02] text-[#E5B842]" : "border-[#2b2512] bg-[#0f0f0f] text-slate-300"}`}>
                  {i === JOURNEY.length - 1 && <Crown className="w-3 h-3 inline mr-1 text-[#E5B842]" />}{s}
                </div>
                {i < JOURNEY.length - 1 && <ChevronRight className="w-4 h-4 text-slate-700" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* QUALIFICATION */}
      {tab === "Qualification" && (
        <div className={CONTAINER}>
          {!qual ? <Loader /> : (
            <div data-testid="wic-qualification">
              <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-2">Road to the World Players Championship</h2>
              <div className="flex flex-wrap items-center gap-3 mb-3 font-mono text-sm">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-950/40 border border-emerald-500/30 text-emerald-300"><b>{qual.ranking_slots}</b> Ranking qualifiers</span>
                <span className="text-slate-600">+</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-fuchsia-950/40 border border-fuchsia-500/30 text-fuchsia-300"><Star className="w-3.5 h-3.5" /> <b>{qual.wildcard_slots}</b> WSTF Wild Cards</span>
                <span className="text-slate-600">=</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#140f02] border border-[#E5B842]/40 text-[#E5B842]"><b>{qual.field_size}</b> World Players Championship players</span>
              </div>
              <div className="text-xs font-mono text-slate-500 mb-3">Qualification cut-off: {new Date(qual.cut_off_at).toLocaleString()} · ranking taken from the ArenaBook World Ranking order.</div>
              {qual.field_fallback_rule && (
                <div data-testid="wic-fallback-note" className="mb-5 rounded-lg border border-[#2b2512] bg-[#0f0f0f] px-4 py-3 text-xs text-slate-400 flex items-start gap-2">
                  <Shield className="w-4 h-4 text-[#E5B842] shrink-0 mt-0.5" />
                  <span>{qual.field_fallback_rule}</span>
                </div>
              )}
              <div className="rounded-xl border border-[#2b2512] overflow-hidden mb-8">
                <table className="w-full text-sm" data-testid="wic-qual-table">
                  <thead className="bg-[#070707] text-slate-500 text-[10px] uppercase tracking-wider font-mono">
                    <tr><th className="text-left px-4 py-2.5 w-14">Seed</th><th className="text-left px-2 py-2.5">Player</th><th className="text-left px-2 py-2.5 hidden sm:table-cell">Country</th><th className="text-left px-2 py-2.5">Method</th><th className="text-right px-4 py-2.5">Rank @ cut-off</th></tr>
                  </thead>
                  <tbody>
                    {qual.field.map((p) => (
                      <tr key={p.seed} data-testid={`wic-qual-${p.seed}`} className="border-t border-[#2b2512]/60 hover:bg-[#161616]/50" style={{ transition: "background-color 0.15s ease" }}>
                        <td className="px-4 py-2.5 font-heading font-black text-slate-300">{p.seed}</td>
                        <td className="px-2 py-2.5"><PlayerChip p={p} /></td>
                        <td className="px-2 py-2.5 font-mono text-slate-500 hidden sm:table-cell">{p.country_iso3}</td>
                        <td className="px-2 py-2.5">
                          {p.method === "WILD_CARD"
                            ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-fuchsia-950/60 text-fuchsia-300 border border-fuchsia-500/40"><Star className="w-3 h-3" /> WILD CARD</span>
                            : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-emerald-950/50 text-emerald-300 border border-emerald-500/30">RANKING</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-slate-400">{p.rank_at_cutoff ? `#${p.rank_at_cutoff}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">First {qual.first_out.length} out · qualification cut-off transparency</div>
              <div className="flex flex-wrap gap-2" data-testid="wic-first-out">
                {qual.first_out.map((p, i) => <span key={i} className="px-3 py-1.5 rounded-lg bg-[#0f0f0f] border border-[#2b2512] text-xs"><PlayerChip p={p} dim /></span>)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* GROUPS */}
      {tab === "Groups" && (
        <div className={CONTAINER}>
          {!groups ? <Loader /> : (
            <div data-testid="wic-groups">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50">Group Stage</h2>
                <div className="inline-flex flex-wrap gap-1 p-1 rounded-xl bg-[#0a0a0a] border border-[#2b2512]">
                  {[["all", "All"], ["ad", "A–D"], ["eh", "E–H"], ["il", "I–L"], ["mp", "M–P"]].map(([k, l]) => (
                    <button key={k} data-testid={`wic-groupband-${k}`} onClick={() => setGroupBand(k)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-heading font-bold uppercase ${groupBand === k ? "bg-[#E5B842] text-[#000000]" : "text-slate-300 hover:text-[#E5B842]"}`}
                      style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="text-[11px] font-mono text-slate-500 mb-4">4 players per group · win {cfg.points?.win} / draw {cfg.points?.draw} / loss {cfg.points?.loss} · top {cfg.advance_per_group} advance</div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {groups.filter((g) => groupBand === "all" || { ad: "ABCD", eh: "EFGH", il: "IJKL", mp: "MNOP" }[groupBand].includes(g.group_id)).map((g) => (
                  <div key={g.group_id} className="rounded-xl border border-[#2b2512] bg-[#0f0f0f] overflow-hidden min-w-0" data-testid={`wic-group-${g.group_id}`}>
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-[#070707]"><Globe2 className="w-4 h-4 text-[#E5B842]" /><span className="font-heading font-black uppercase text-slate-100">Group {g.group_id}</span></div>
                    <div className="overflow-x-auto no-scrollbar"><table className="w-full text-sm">
                      <thead className="text-slate-500 text-[9px] uppercase font-mono"><tr><th className="text-left px-3 py-1.5 w-6">#</th><th className="text-left px-1 py-1.5">Player</th><th className="px-1 py-1.5">P</th><th className="px-1 py-1.5">W-D-L</th><th className="px-3 py-1.5 text-[#E5B842]">Pts</th></tr></thead>
                      <tbody>
                        {g.table.map((r) => (
                          <tr key={r.id} className={`border-t border-[#2b2512]/60 ${r.advances ? "" : "opacity-60"}`}>
                            <td className="px-3 py-2 relative font-heading font-black text-slate-200">
                              {r.advances && <span className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" aria-label="Qualified" />}
                              {r.position}
                            </td>
                            <td className="px-1 py-2"><PlayerChip p={r} dim={!r.advances} className="text-xs" />{r.advances && <CheckCircle2 className="w-3 h-3 text-emerald-400 inline ml-1" aria-label="Qualified" />}</td>
                            <td className="px-1 py-2 text-center font-mono text-slate-400">{r.played}</td>
                            <td className="px-1 py-2 text-center font-mono text-slate-400">{r.won}-{r.drawn}-{r.lost}</td>
                            <td className="px-3 py-2 text-center font-heading font-black text-[#E5B842]">{r.points}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-[11px] font-mono text-slate-600 flex items-center gap-2"><span className="w-3 h-3 bg-emerald-500 rounded-sm inline-block" /> Qualified — top {cfg.advance_per_group} of each group advance to the Round of {cfg.groups * cfg.advance_per_group}</div>
            </div>
          )}
        </div>
      )}

      {/* BRACKET — structured round columns (no parent/child link data exists → no fabricated connectors) */}
      {tab === "Bracket" && (
        <div className={`${WIDE} overflow-x-hidden`}>
          {!bracket ? <Loader /> : (
            <div data-testid="wic-bracket">
              <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-2">Knockout Stage</h2>
              <p className="text-[11px] font-mono text-slate-500 mb-5">32-player single-elimination · blind 1v1 ArenaBook duels · a level duel is settled by ArenaBook Extra Duels.</p>
              <div className="overflow-x-auto no-scrollbar pb-4">
                <div className="flex gap-4 min-w-max items-stretch">
                  {bracket.map((rnd) => (
                    <div key={rnd.round_code} className={`w-[250px] shrink-0 flex flex-col ${rnd.round_code === "F" ? "justify-center" : "justify-around"}`}>
                      <div className={`text-[10px] uppercase tracking-[0.2em] font-mono mb-3 ${rnd.round_code === "F" ? "text-[#E5B842]" : "text-slate-400"}`}>{rnd.label}</div>
                      <div className={rnd.round_code === "F" ? "" : "space-y-3"}>
                        {rnd.ties.map((t) => {
                          const big = rnd.round_code === "F";
                          return (
                            <div key={t.bracket_index} data-testid={`wic-tie-${rnd.round_code}-${t.bracket_index}`}
                              className={`rounded-xl border bg-[#0f0f0f] p-2.5 ${big ? "foil-border p-4" : "border-[#2b2512]"}`}>
                              {big && <div className="text-[10px] font-mono uppercase tracking-wider text-[#E5B842] mb-2 text-center">World Players Championship Final</div>}
                              {[t.p1, t.p2].map((pl) => {
                                const win = t.winner_player_id === pl?.id;
                                return (
                                  <div key={pl?.id || Math.random()} className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded ${win ? "bg-[#17210f] text-[#E5B842] font-bold" : "text-slate-400"}`}>
                                    <PlayerChip p={pl} dim={!win} className="text-xs" />
                                    {win && <Crown className="w-3.5 h-3.5 shrink-0" />}
                                  </div>
                                );
                              })}
                              {t.extra_rounds > 0 && <div className="text-[9px] text-fuchsia-300 mt-1 flex items-center gap-1 px-2"><Flame className="w-3 h-3" /> +{t.extra_rounds} extra duel{t.extra_rounds > 1 ? "s" : ""}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CHAMPION */}
      {tab === "Champion" && (
        <div className={CONTAINER}>
          {!champ ? <Loader /> : !champ.crowned ? <Empty label="The champion has not been crowned yet — the tournament is still in progress." /> : (
            <div data-testid="wic-champion">
              <div className="relative overflow-hidden rounded-2xl foil-border bg-gradient-to-br from-[#2a2410] to-[#070707] p-8 sm:p-10 mb-8 text-center">
                <div className="absolute inset-0 mesh-grid opacity-[0.18] pointer-events-none" />
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 120% at 50% 0%, rgba(212,175,55,0.22) 0%, transparent 60%)" }} />
                <Crown className="relative w-16 h-16 text-[#E5B842] mx-auto mb-3" />
                <div className="relative text-[11px] uppercase tracking-[0.32em] text-[#E5B842] font-mono">World Champion {champ.edition}</div>
                <div className="relative font-heading text-3xl sm:text-5xl font-black text-slate-50 mt-2 leading-[1.05] break-words flex justify-center">
                  <PlayerChip p={champ.champion} wrap className="justify-center" />
                </div>
                <div className="relative text-sm text-slate-400 mt-3 flex flex-wrap items-center justify-center gap-1.5 min-w-0 break-words"><Flag className="w-3.5 h-3.5 shrink-0" /> <span className="break-words">{champ.champion.country_iso3} · Finalist: {champ.finalist?.display_name} ({champ.finalist?.country_iso3})</span></div>
                <a data-testid="wic-share-btn" href={`${process.env.REACT_APP_BACKEND_URL}/api/share/champion/wic`} target="_blank" rel="noreferrer"
                  className="relative inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-lg bg-[#E5B842] text-[#000000] text-sm font-bold uppercase tracking-wide hover:brightness-110" style={{ transition: "filter 0.15s ease" }}>
                  <Trophy className="w-4 h-4" /> Share Champion Card
                </a>
              </div>

              {/* CHAMPION'S PATH */}
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-4 flex items-center gap-2"><Swords className="w-5 h-5 text-[#E5B842]" /> Champion's Path</h2>
              <div className="flex flex-col lg:flex-row lg:items-stretch gap-3 mb-10" data-testid="wic-champion-path">
                {champ.path.filter((s) => s.stage !== "GROUP").map((s, i, arr) => {
                  const opp = idMap[s.opponent_id];
                  return (
                    <div key={i} className="contents lg:flex lg:items-center">
                      <div className="flex-1 rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4 min-w-0">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-[#E5B842] mb-2">{s.round}</div>
                        <div className="text-[10px] font-mono text-slate-500 uppercase mb-1">vs</div>
                        {opp ? <PlayerChip p={opp} className="text-sm" /> : <span className="text-sm text-slate-400 font-mono">Opponent</span>}
                        <div className="mt-2 inline-flex items-center gap-1.5">
                          <span className="inline-flex items-center gap-1 text-emerald-300 font-heading font-bold uppercase text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> {s.won ? "Won" : "—"}</span>
                          {s.extra_rounds > 0 && <span className="text-[10px] text-fuchsia-300 font-mono inline-flex items-center gap-0.5"><Flame className="w-3 h-3" />+{s.extra_rounds} xd</span>}
                        </div>
                      </div>
                      {i < arr.length - 1 && <div className="hidden lg:flex items-center px-1 text-slate-700"><ChevronRight className="w-5 h-5" /></div>}
                    </div>
                  );
                })}
              </div>

              {/* TIER POINTS */}
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-1 flex items-center gap-2"><Star className="w-5 h-5 text-[#E5B842]" /> WSTF 3000 Tier Points</h2>
              <p className="text-[11px] font-mono text-slate-500 mb-4">This is a WSTF 3000 tier event — ranking tier points awarded by finishing stage (Champion {cfg.tier?.CHAMPION} · Finalist {cfg.tier?.FINALIST} · Semifinalists {cfg.tier?.SEMI_FINAL} each · QF {cfg.tier?.QUARTER_FINAL} · R16 {cfg.tier?.ROUND_16}). Values shown as settled by the championship engine.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="wic-podium">
                {champ.podium.map((p, i) => (
                  <div key={i} className="rounded-xl border border-[#2b2512] bg-[#0f0f0f] p-4 min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{p.stage_reached}</div>
                    <div className="text-sm text-slate-100 my-1"><PlayerChip p={p} /></div>
                    <div className="font-heading text-2xl font-black text-[#E5B842] tabular-nums">{p.tier_points}</div>
                    <div className="text-[9px] font-mono uppercase text-slate-600">tier points</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, Icon, gold }) {
  return (
    <div className={`rounded-xl border p-5 ${gold ? "border-[#E5B842]/40 bg-[#140f02]" : "border-[#2b2512] bg-[#0f0f0f]"}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-1.5 flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5 text-[#E5B842]" />}{label}</div>
      <div className={`font-heading text-xl sm:text-2xl font-black break-words ${gold ? "text-[#E5B842]" : "text-slate-50"}`}>{String(value)}</div>
    </div>
  );
}
